import { API } from '@aws-amplify/api'
import {
  addInboxData,
  addCaseDetails,
  getCaseHistory,
  addPathologistPendingData,
  addPathologistCompletedData,
  updateFetchHistoryInProgress,
  updateFetchInboxDataInProgress,
  addPathologistRecentlyRelesedData,
  updateCompleteCaseData,
  updateLaunchPremerisData,
  updateFreezeReportStatus,
  updateFetchRecentlyReleasedData,
  updatePathologistsReceiveSlidesData,
  updateFetchPathologistCompleteInProgress,
  updateFetchPathologistPendingInProgress,
  addFilterPayload,
  errorMessageDisplay,
} from '../actions'
import { WOMA_API_GATEWAY_NAME } from '../../constants/aws'
import { PathologistRequest } from './../../interfaces/inbox'

import {
  ADD_INBOX_DATA,
  INBOX_ERROR,
  RECEIVE_PATHOLOGISTS_SLIDES_DATA,
  RECEIVE_PATHOLOGISTS_SLIDES_DATA_ERROR,
  COMPLETE_CASE_DATA,
  COMPLETE_CASE_DATA_ERROR,
} from './../actionConstants'

interface ReceivedSlidesRequest {
  id?: string | null | string[]
  payload?: string | null | string[]
}

interface StartCaseRequest {
  id?: string | null | string[]
  caseid?: string | null | string[]
}

interface ConsultRequest {
  id?: string | null | string[]
  newUserId: string | null | string[]
  caseId: string | null | string[]
}
export const fetchInboxData =
  ({
    id,
    pageIndex,
    pageSize,
    filterField,
    filterValue,
    caseView,
    startDate,
    endDate,
    sortOrder,
    sortField,
  }: PathologistRequest) =>
  async (dispatch) => {
    if (!id || id instanceof Array) return

    try {
      const options = {
        headers: {},
        /**
         * response: true => return the entire Axios response object instead
         * of only response.data
         * */
        response: false,
        queryStringParameters: {
          page_number: pageIndex ? pageIndex : 1,
          page_size: pageSize ? pageSize : 10,
          filterField: filterField,
          filterValue: filterValue,
          caseView: caseView,
          startDate: startDate,
          endDate: endDate,
          sortOrder: sortOrder,
          sortField: sortField,
        },
      }
      const filterParameters = options.queryStringParameters
      filterParameters['id'] = id
      dispatch(addFilterPayload({ payload: filterParameters }))
      const apiName = `case-management/pathologist/all-cases/${id}`
      dispatch(updateFetchInboxDataInProgress(true))
      const result = await API.get(WOMA_API_GATEWAY_NAME, `/${apiName}`, options)

      if (result?.data) {
        dispatch(addInboxData({ payload: result, mdid: id, action: ADD_INBOX_DATA }))
      }
    } catch (err) {
      dispatch(addInboxData({ payload: {}, mdid: id, action: INBOX_ERROR }))
      console.log('Cases Error', err)
    } finally {
      dispatch(updateFetchInboxDataInProgress(false))
    }
  }

export const getCaseDetails = (id: PathologistRequest) => async (dispatch) => {
  if (!id || id instanceof Array) return

  try {
    const options = {
      headers: {},
      /**
       * response: true => return the entire Axios response object instead
       * of only response.data
       * */
      response: false,
    }
    const apiName = `caseDetails/${id}`
    const result = await API.get(WOMA_API_GATEWAY_NAME, `/${apiName}`, options)
    if (result?.data) {
      dispatch(addCaseDetails({ payload: result, mdid: id }))
    }
  } catch (err) {
    console.log('Cases Error', err)
  }
}

export const fetchCaseHistory = (id: PathologistRequest, role) => async (dispatch) => {
  if (!id || id instanceof Array) return

  try {
    const options = {
      headers: {},
      /**
       * response: true => return the entire Axios response object instead
       * of only response.data
       * */
      response: false,
    }
    let apiName = 'case-management/history'
    if (role === 'NL_Pathologist') apiName = 'case-management/pathologist-case-history'
    dispatch(updateFetchHistoryInProgress(true))
    const result = await API.get(WOMA_API_GATEWAY_NAME, `/${apiName}/${id}`, options)
    if (result?.data) {
      dispatch(getCaseHistory({ payload: result, mdid: id }))
    }
  } catch (err) {
    console.log('Cases Error', err)
  } finally {
    dispatch(updateFetchHistoryInProgress(false))
  }
}

export const fetchCompletedData =
  ({
    id,
    pageIndex,
    pageSize,
    filterField,
    filterValue,
    caseView,
    startDate,
    endDate,
    sortOrder,
    sortField,
  }: PathologistRequest) =>
  async (dispatch) => {
    if (!id || id instanceof Array) return

    try {
      const options = {
        headers: {},
        /**
         * response: true => return the entire Axios response object instead
         * of only response.data
         * */
        response: false,
        queryStringParameters: {
          page_number: pageIndex ? pageIndex : 1,
          page_size: pageSize ? pageSize : 10,
          filterField: filterField,
          filterValue: filterValue,
          caseView: caseView,
          startDate: startDate,
          endDate: endDate,
          sortOrder: sortOrder,
          sortField: sortField,
        },
      }
      const apiName = `case-management/pathologist/all-cases`
      dispatch(updateFetchPathologistCompleteInProgress(true))
      const result = await API.get(WOMA_API_GATEWAY_NAME, `/${apiName}/${id}`, options)

      if (result?.data) {
        result.data = result?.data.map((value, i) => {
          value.rowId = i + 1
          return value
        })

        dispatch(addPathologistCompletedData({ payload: result, mdid: id }))
      }
    } catch (err) {
      console.log('Cases Error', err)
    } finally {
      dispatch(updateFetchPathologistCompleteInProgress(false))
    }
  }

export const fetchRecentlyReleasedData =
  ({
    id,
    pageIndex,
    pageSize,
    filterField,
    filterValue,
    caseView,
    startDate,
    endDate,
    sortOrder,
    sortField,
  }: PathologistRequest) =>
  async (dispatch) => {
    if (!id || id instanceof Array) return

    try {
      const options = {
        headers: {},
        /**
         * response: true => return the entire Axios response object instead
         * of only response.data
         * */
        response: false,
        queryStringParameters: {
          page_number: pageIndex ? pageIndex : 1,
          page_size: pageSize ? pageSize : 10,
          filterField: filterField,
          filterValue: filterValue,
          caseView: caseView,
          startDate: startDate,
          endDate: endDate,
          sortOrder: sortOrder,
          sortField: sortField,
        },
      }
      const apiName = `case-management/pathologist/all-cases/${id}`
      dispatch(updateFetchRecentlyReleasedData({ inProgress: true }))
      const result = await API.get(WOMA_API_GATEWAY_NAME, `/${apiName}`, options)

      if (result?.data) {
        dispatch(addPathologistRecentlyRelesedData({ payload: result, mdid: id }))
      }
    } catch (err) {
      console.log('Cases Error', err)
    } finally {
      dispatch(updateFetchRecentlyReleasedData({ inProgress: false }))
    }
  }
export const receivePathologistsSlidesByCaseId =
  ({ payload, id }: ReceivedSlidesRequest) =>
  async (dispatch) => {
    let res = false
    let result = {}
    try {
      const options = {
        headers: {},
        response: false,
        queryStringParameters: {
          caseid: payload,
        },
      }
      const apiName = '/case-management/pathologist/receive-slides'
      result = await API.put(WOMA_API_GATEWAY_NAME, `${apiName}/${id}?`, options)
        .then(() => (res = true))
        .catch((error) => dispatch(errorMessageDisplay({ payload: JSON.parse(error.response.data).message })))
    } catch (err) {
      res = false
      console.log('Cases Error', err)
    } finally {
      await dispatch(
        updatePathologistsReceiveSlidesData({
          payload: result,
          action: res ? RECEIVE_PATHOLOGISTS_SLIDES_DATA : RECEIVE_PATHOLOGISTS_SLIDES_DATA_ERROR,
        }),
      )
    }
  }

interface SendCompleteCaseRequest {
  id?: string | null | string[]
  caseid?: string | null | string[]
  indicator?: string | null | string[]
}

export const sendCompleteCaseDetails =
  ({ id, caseid, indicator }: SendCompleteCaseRequest) =>
  async (dispatch) => {
    let action = ''
    try {
      const options = {
        headers: {},
        response: false,
        queryStringParameters: {
          caseid,
          indicator,
        },
      }
      const apiName = `/case-management/premeris/case-status-resulted`
      const result = await API.put(WOMA_API_GATEWAY_NAME, `${apiName}/${id}?`, options)
      action = 'COMPLETE_CASE_DATA'
    } catch (err) {
      action = 'COMPLETE_CASE_DATA_ERROR'
      console.log('Cases Error', err)
    } finally {
      dispatch(updateCompleteCaseData({ action: action }))
    }
  }

export const sendStartCaseDetails =
  ({ id, caseid }: StartCaseRequest) =>
  async (dispatch) => {
    let res = false
    let action = ''
    let result = {}
    try {
      const options = {
        headers: {},
        response: false,
        queryStringParameters: {
          caseid,
        },
      }
      const apiName = `/case-management/pathologist/open-trf-case`
      result = await API.put(WOMA_API_GATEWAY_NAME, `${apiName}/${id}?`, options)
      action = 'LAUNCH_PREMERIS_DATA'
      res = true
      //dispatch(updatePathologistsReceiveSlidesData({ payload: result }))
    } catch (err) {
      res = false
      action = 'LAUNCH_PREMERIS_DATA_ERROR'
      console.log('Cases Error', err)
    } finally {
      dispatch(updateLaunchPremerisData({ action: action }))
    }
  }

export const freezeCase =
  ({ id, caseid }: StartCaseRequest) =>
  async (dispatch) => {
    let action = ''
    try {
      const options = {
        headers: {},
        response: false,
        queryStringParameters: {
          caseid,
        },
      }
      const apiName = `/case-management/pathologist/update-case-to-freeze-reporting`
      const result = await API.put(WOMA_API_GATEWAY_NAME, `${apiName}/${id}?`, options)
      action = 'UPDATE_FREEZE_STATUS_SUCCESS'
    } catch (err) {
      action = 'UPDATE_FREEZE_STATUS_ERROR'
    } finally {
      dispatch(updateFreezeReportStatus({ action: action }))
    }
  }

export const consultAssign =
  ({ id, newUserId, caseId }: ConsultRequest) =>
  async (dispatch) => {
    const apiName = '/case-management/consult'
    try {
      const options = {
        headers: {},
        response: false,
        queryStringParameters: {
          caseid: caseId,
          consultid: newUserId,
        },
      }
      const result = await API.post(WOMA_API_GATEWAY_NAME, `${apiName}/${id}?`, options)
    } catch (err) {
      console.log('Cases Error', err)
    }
  }
